import React, { useEffect, useRef } from 'react';
import { styles } from './HeroBanner.css';
import { useMediaQuery } from '@jbhi-fi/lanyard-ui/utils/useMediaQuery';
import { classNames } from '@jbhi-fi/lanyard-ui/global/utils';
import { LoadingSkeleton } from '../LoadingSkeleton/LoadingSkeleton';
import { palette } from '@jbhi-fi/lanyard-ui/themes';
import { useTrackingContext } from '../Tracking';
import { useIsOnScreen } from '../hooks/useIsOnScreen/useIsOnScreen';
import { TrackingData, trackingDataToDataAttributes } from './utils';

type Props = {
  variant?: 'homepage' | 'collection';
  imageMobile?: React.ReactNode;
  isContained?: boolean;
  isHeroBanner?: boolean;
  image: React.ReactNode;
  href: string;
  trackingData?: TrackingData;
  testId?: string;
};

export type HeroBannerProps = ({ isLoading?: false } & Props) | ({ isLoading: true } & Partial<Props>);

export const HeroBanner = ({
  variant,
  image,
  imageMobile,
  isHeroBanner,
  isContained = isHeroBanner,
  href,
  isLoading,
  trackingData,
  testId,
}: HeroBannerProps) => {
  const isMobile = useMediaQuery('(max-width: 768px)');
  const anchorRef = useRef<HTMLAnchorElement>(null);
  const isOnScreen = useIsOnScreen(anchorRef, { persistOnceSeen: true });
  const { tracker } = useTrackingContext();

  const haveTrackedImpression = useRef<boolean>(false);
  useEffect(() => {
    if (isOnScreen && !isLoading && !haveTrackedImpression.current) {
      haveTrackedImpression.current = true;
      tracker.trackCustomEvent({ event: 'internal promotion impression', gaipElement: anchorRef.current });
    }
  }, [isOnScreen, isLoading, tracker]);

  const handleClick: React.MouseEventHandler<HTMLAnchorElement> = () => {
    tracker.trackCustomEvent({ event: 'internal promotion click', gaipElement: anchorRef.current });
  };

  const getBannerVariant = () => {
    if (variant === 'homepage' && isHeroBanner) {
      return 'homepageHeroBanner';
    } else if (variant === 'homepage' && !isHeroBanner) {
      return 'homepageLegacyBanner';
    } else if (variant === 'collection' && isHeroBanner) {
      return 'collectionHeroBanner';
    } else if (variant === 'collection' && !isHeroBanner) {
      return 'collectionLegacyBanner';
    }
  };

  const bannerVariant = getBannerVariant();

  return (
    <a
      className={classNames(
        styles.container({ isHeroBanner, bannerVariant }),
        styles.isContainedStyles({ isContained })
      )}
      href={href ?? ''}
      ref={anchorRef}
      onClick={handleClick}
      {...trackingDataToDataAttributes(
        trackingData ? { ...trackingData, category: trackingData.category || tracker.category } : undefined
      )}
      data-testid={testId}
    >
      {isLoading ? (
        <LoadingSkeleton.Box className={styles.loadingBox} height="0px" backgroundColor={palette.secondary.main} />
      ) : isMobile ? (
        imageMobile ?? image
      ) : (
        image
      )}
    </a>
  );
};
